import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayToetsAvatar } from '../../mascot/DjayToetsAvatar';
import type { AssessmentReport } from '../types';
interface AssignedAssessmentCompleteScreenProps {
  report: AssessmentReport;
  onExit: () => void;
}
export function AssignedAssessmentCompleteScreen({
  report,
  onExit
}: AssignedAssessmentCompleteScreenProps) {
  const totalQuestions = report.score.totalQuestions;
  const correctCount = report.score.correctCount;
  const score = report.score.percentage;
  const answers = report.answers ?? [];
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4" data-id="element-1166">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-md" data-id="element-1167">
        <Card className="p-8 text-center shadow-lg border-orange-100" data-id="element-1168">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="mb-6" data-id="element-1169">
            <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-1170" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-1171">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4" data-id="element-1172">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-1173" />
            </div>

            <h1 className="text-2xl font-black text-slate-900 mb-2" data-id="element-1174">
              Toets klaar! 🎉
            </h1>
            <p className="text-slate-600 mb-6" data-id="element-1175">
              Goed gedaan! Je hebt de toets afgerond.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-slate-50 rounded-xl p-4 mb-4" data-id="element-1176">
            <div className="grid grid-cols-2 gap-4" data-id="element-1177">
              <div data-id="element-1178">
                <div className="text-2xl font-black text-orange-600" data-id="element-1179">
                  {Math.round(score)}%
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1180">
                  Score
                </div>
              </div>
              <div data-id="element-1181">
                <div className="text-2xl font-black text-slate-800" data-id="element-1182">
                  {correctCount}/{totalQuestions}
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1183">
                  Goed beantwoord
                </div>
              </div>
            </div>
          </motion.div>

          {/* Per-question goed/fout summary */}
          {answers.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="mb-6" data-id="element-1184">
              <h2 className="text-sm font-bold text-slate-700 mb-3 text-left" data-id="element-1185">
                Overzicht per vraag
              </h2>
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2" data-id="element-1186">
                {answers.map((answer, index) => <motion.div key={answer.questionId} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.75 + index * 0.03
            }} className={`
                      flex flex-col items-center justify-center rounded-lg py-2 px-1
                      ${answer.isCorrect ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}
                    `} data-id="element-1187">
                    <span className="text-xs font-bold mb-0.5" data-id="element-1188">
                      {index + 1}
                    </span>
                    {answer.isCorrect ? <CheckCircle2 className="w-4 h-4" data-id="element-1189" /> : <XCircle className="w-4 h-4" data-id="element-1190" />}
                  </motion.div>)}
              </div>
            </motion.div>}

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} data-id="element-1191">
            <p className="text-sm text-slate-500 mb-5" data-id="element-1192">
              Je docent kan de resultaten bekijken in het leerkrachtportaal.
            </p>

            <Button onClick={onExit} className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="lg" data-id="element-1193">
              Terug naar home
              <ArrowRight className="w-4 h-4 ml-2" data-id="element-1194" />
            </Button>
          </motion.div>
        </Card>
      </motion.div>
    </div>;
}